import { useQuery } from "@apollo/client";
import type { ApolloQueryResult } from "@apollo/client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GET_MAILMAP_RECORDS_QUERY } from "../queries";
import type { IMailmapRecord } from "features/mailmap/types";
import type { MailmapRecordsQuery } from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

interface IUseMailmapRecordsProps {
  organizationId: string;
}

interface IUseMailmapRecordsReturn {
  records: IMailmapRecord[];
  refetch: () => Promise<ApolloQueryResult<MailmapRecordsQuery>>;
}

const useMailmapRecords = ({
  organizationId,
}: IUseMailmapRecordsProps): IUseMailmapRecordsReturn => {
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();

  const { data, refetch } = useQuery(GET_MAILMAP_RECORDS_QUERY, {
    fetchPolicy: "cache-first",
    onCompleted: (): void => {
      addAuditEvent("Organization.Mailmap", organizationId);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Error loading mailmap records:", error);
        msgError(t("mailmap.errorGetMailmapEntries"), t("mailmap.errorTitle"));
      });
    },
    variables: {
      organizationId,
    },
  });

  const records = useMemo((): IMailmapRecord[] => {
    return (
      data?.mailmapRecords.map(
        (record, index): IMailmapRecord => ({
          id: index.toString(),
          mailmapEntryEmail: record.mailmapEntryEmail,
          mailmapEntryName: record.mailmapEntryName,
          mailmapSubentryEmail: record.mailmapSubentryEmail,
          mailmapSubentryName: record.mailmapSubentryName,
        }),
      ) ?? []
    );
  }, [data]);

  return { records, refetch };
};

export { useMailmapRecords };
